import { supabase } from '../lib/supabase'

export interface MonthlyIncomeData {
  month: string
  year: number
  income: number
  invoiceCount: number
}

export interface ProjectedIncomeAnalysis {
  currentMonthIncome: number
  projectedAnnualIncome: number
  confidence: number
  trend: 'increasing' | 'decreasing' | 'stable'
  trendPercentage: number
  monthsOfData: number
  recommendation: string
}

class AnalyticsService {
  // Get monthly income data for the past 12 months
  async getMonthlyIncomeHistory(userId: string): Promise<MonthlyIncomeData[]> {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized')
      }

      // Get invoices from the past 12 months
      const twelveMonthsAgo = new Date()
      twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12)

      // Get regular invoices
      const { data: invoices, error } = await supabase
        .from('invoices')
        .select('total_amount, created_at, payment_status, payment_date')
        .eq('user_id', userId)
        .eq('payment_status', 'paid')
        .gte('created_at', twelveMonthsAgo.toISOString())
        .order('created_at', { ascending: true })

      if (error) throw error

      // Get uploaded invoices
      const { data: uploadedInvoices, error: uploadedError } = await supabase
        .from('uploaded_invoices')
        .select('amount, paid_amount, uploaded_at, payment_status, paid_date')
        .eq('user_id', userId)
        .eq('payment_status', 'paid')
        .gte('uploaded_at', twelveMonthsAgo.toISOString())
        .order('uploaded_at', { ascending: true })

      if (uploadedError) throw uploadedError

      // Group by month and calculate totals
      const monthlyData: { [key: string]: MonthlyIncomeData } = {}

      // Process regular invoices
      invoices?.forEach(invoice => {
        // Use payment_date if available, otherwise fall back to created_at
        const dateToUse = invoice.payment_date || invoice.created_at
        const date = new Date(dateToUse)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthKey,
            year: date.getFullYear(),
            income: 0,
            invoiceCount: 0
          }
        }
        
        monthlyData[monthKey].income += invoice.total_amount || 0
        monthlyData[monthKey].invoiceCount += 1
      })

      // Process uploaded invoices
      uploadedInvoices?.forEach(invoice => {
        // Use paid_date if available, otherwise fall back to uploaded_at
        const dateToUse = invoice.paid_date || invoice.uploaded_at
        const date = new Date(dateToUse)
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = {
            month: monthKey,
            year: date.getFullYear(),
            income: 0,
            invoiceCount: 0
          }
        }
        
        // Use paid_amount if available, otherwise use amount
        const amount = invoice.paid_amount || invoice.amount || 0
        monthlyData[monthKey].income += amount
        monthlyData[monthKey].invoiceCount += 1
      })

      return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month))
    } catch (error) {
      console.error('Error fetching monthly income history:', error)
      return []
    }
  }

  // Calculate projected annual income with AI-powered analysis
  async calculateProjectedAnnualIncome(userId: string): Promise<ProjectedIncomeAnalysis> {
    const monthlyData = await this.getMonthlyIncomeHistory(userId)
    
    if (monthlyData.length === 0) {
      return {
        currentMonthIncome: 0,
        projectedAnnualIncome: 0,
        confidence: 0,
        trend: 'stable',
        trendPercentage: 0,
        monthsOfData: 0,
        recommendation: 'Start creating and sending invoices to generate income projections.'
      }
    }

    const currentMonth = new Date()
    const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}`
    const currentMonthData = monthlyData.find(m => m.month === currentMonthKey)
    const currentMonthIncome = currentMonthData?.income || 0

    // Calculate trend analysis
    const recentMonths = monthlyData.slice(-6) // Last 6 months for trend
    const olderMonths = monthlyData.slice(-12, -6) // Previous 6 months for comparison

    let trend: 'increasing' | 'decreasing' | 'stable' = 'stable'
    let trendPercentage = 0

    if (recentMonths.length >= 3 && olderMonths.length >= 3) {
      const recentAvg = recentMonths.reduce((sum, m) => sum + m.income, 0) / recentMonths.length
      const olderAvg = olderMonths.reduce((sum, m) => sum + m.income, 0) / olderMonths.length

      if (olderAvg > 0) {
        trendPercentage = ((recentAvg - olderAvg) / olderAvg) * 100
        
        if (trendPercentage > 10) {
          trend = 'increasing'
        } else if (trendPercentage < -10) {
          trend = 'decreasing'
        }
      }
    }

    // Calculate projected annual income using multiple methods
    let projectedAnnualIncome = 0
    let confidence = 0

    if (monthlyData.length >= 3) {
      // Method 1: Simple average of available months * 12
      const avgMonthlyIncome = monthlyData.reduce((sum, m) => sum + m.income, 0) / monthlyData.length
      const simpleProjection = avgMonthlyIncome * 12

      // Method 2: Weighted average (recent months have more weight)
      let weightedSum = 0
      let totalWeight = 0
      monthlyData.forEach((month, index) => {
        const weight = index + 1 // More recent months get higher weight
        weightedSum += month.income * weight
        totalWeight += weight
      })
      const weightedProjection = (weightedSum / totalWeight) * 12

      // Method 3: Trend-adjusted projection
      let trendAdjustedProjection = simpleProjection
      if (trend === 'increasing') {
        trendAdjustedProjection = simpleProjection * (1 + Math.min(trendPercentage / 100, 0.5))
      } else if (trend === 'decreasing') {
        trendAdjustedProjection = simpleProjection * (1 + Math.max(trendPercentage / 100, -0.3))
      }

      // Combine methods with weights based on data quality
      const dataQualityWeight = Math.min(monthlyData.length / 12, 1)
      projectedAnnualIncome = (
        simpleProjection * 0.3 +
        weightedProjection * 0.4 +
        trendAdjustedProjection * 0.3
      ) * dataQualityWeight

      // Calculate confidence based on data consistency and amount
      const incomeVariability = this.calculateVariability(monthlyData.map(m => m.income))
      const dataAmountFactor = Math.min(monthlyData.length / 6, 1) // More data = higher confidence
      const consistencyFactor = Math.max(0, 1 - incomeVariability) // Lower variability = higher confidence
      
      confidence = Math.round((dataAmountFactor * 0.6 + consistencyFactor * 0.4) * 100)
    } else {
      // Limited data - use simple multiplication
      projectedAnnualIncome = currentMonthIncome * 12
      confidence = 25 // Low confidence with limited data
    }

    // Generate recommendation
    let recommendation = ''
    if (monthlyData.length < 3) {
      recommendation = 'Generate more invoices over the next few months to improve projection accuracy.'
    } else if (trend === 'increasing') {
      recommendation = `Great momentum! Your income is trending upward by ${Math.round(Math.abs(trendPercentage))}%. Consider expanding your services or raising rates.`
    } else if (trend === 'decreasing') {
      recommendation = `Income has decreased by ${Math.round(Math.abs(trendPercentage))}%. Focus on client retention and new business development.`
    } else {
      recommendation = 'Your income is stable. Consider strategies to increase revenue such as premium packages or additional services.'
    }

    return {
      currentMonthIncome,
      projectedAnnualIncome: Math.round(projectedAnnualIncome),
      confidence: Math.max(0, Math.min(100, confidence)),
      trend,
      trendPercentage: Math.round(trendPercentage * 10) / 10,
      monthsOfData: monthlyData.length,
      recommendation
    }
  }

  // Calculate coefficient of variation to measure income variability
  private calculateVariability(incomes: number[]): number {
    if (incomes.length === 0) return 0
    
    const mean = incomes.reduce((sum, income) => sum + income, 0) / incomes.length
    if (mean === 0) return 0
    
    const variance = incomes.reduce((sum, income) => sum + Math.pow(income - mean, 2), 0) / incomes.length
    const standardDeviation = Math.sqrt(variance)
    
    return standardDeviation / mean // Coefficient of variation
  }
}

export const analyticsService = new AnalyticsService() 